/**
 * Currently, very few legacy (connect) macros provide their expected rendered height/width
 * values in their macro parameters. Of those that do, there's a slight but consistent
 * margin of error for the height value. Others that don't render dynamic content we can
 * set a default height for (such as Miro embed).
 *
 * Any changes here should be reflected in @confluence/fabric-extension-legacy-macro-renderer
 */
const DRAW_IO_HEIGHT_OFFSET = 52;
const GOOGLE_DRIVE_EMBED_FILE_OFFSET_HEIGHT = 79;
const PLANTUML_OFFSET_HEIGHT = 40;

// Default sizes
const MIRO_EMBED_DEFAULT_HEIGHT = 450;
const EASY_MATH_BLOCK_DEFAULT_HEIGHT = 47;
const MATH_BLOCK_DEFAULT_HEIGHT = 58;
const PAGE_SIGNATURES_DEFAULT_HEIGHT = 23;
const QUESTIONS_LIST_DEFAULT_HEIGHT = 800;
const JIRA_ROADMAP_DEFAULT_HEIGHT = 600;
const LUCID_CHART_DEFAULT_HEIGHT = 500;
const GLIFFY_DEFAULT_HEIGHT = 150;
const AURA_DIVIDER_DEFAULT_HEIGHT = 21;
const AURA_CARDS_DEFAULT_HEIGHT = 226;

const getHeightFromParams = (node: any) => {
	if (!node) return;

	const extensionKey = node.extensionKey;
	const params = node.parameters?.macroParams;

	/**
	 * DrawIo will resize diagrams based on the viewport they are rendered in
	 * if the diagram's width is greater than 530px. This means that the natural dimensions
	 * provided in macro parameters could be the incorrect height to reserve so we will
	 * conservatively exclude them.
	 */
	if (
		extensionKey === 'drawio' ||
		extensionKey === 'inc-drawio' ||
		extensionKey === 'drawio-sketch'
	) {
		const diagramWidth = Number(params?.width?.value);
		const zoomFactor = Number(params?.zoom?.value);

		if ((diagramWidth && diagramWidth > 530) || zoomFactor !== 1) {
			// Exclude wide diagrams due to allow resizing by vendor
			return;
		}
	}

	if (extensionKey === 'plantumlcloud') return params?.originalHeight?.value;

	return params?.height?.value;
};

export const useConnectExtensionViewportSize = (
	node?: any,
): {
	viewportHeight: number | undefined;
} => {
	const macroHeight = getHeightFromParams(node);
	const extensionKey = node?.extensionKey;

	let height: number | undefined = Number(macroHeight);

	if (!Number.isNaN(height)) {
		// for extensions that provide a number, add their offset
		switch (extensionKey) {
			case 'drawio':
			case 'inc-drawio':
			case 'drawio-sketch':
				height += DRAW_IO_HEIGHT_OFFSET;
				break;
			case 'lref-gdrive-embedded-file':
				height += GOOGLE_DRIVE_EMBED_FILE_OFFSET_HEIGHT;
				break;
			case 'plantumlcloud':
				height += PLANTUML_OFFSET_HEIGHT;
				break;
			default:
				break;
		}
	} else {
		// for extensions that don't resize based on their content, we can set a default height
		switch (extensionKey) {
			case 'gliffy':
				height = GLIFFY_DEFAULT_HEIGHT;
				break;
			case 'aura-divider':
				height = AURA_DIVIDER_DEFAULT_HEIGHT;
				break;
			case 'aura-cards':
				height = AURA_CARDS_DEFAULT_HEIGHT;
				break;
			case 'miro-macro-resizing':
				height = MIRO_EMBED_DEFAULT_HEIGHT;
				break;
			case 'easy-math-block':
				height = EASY_MATH_BLOCK_DEFAULT_HEIGHT;
				break;
			case 'mathblock':
				height = MATH_BLOCK_DEFAULT_HEIGHT;
				break;
			case 'page-signatures':
				height = PAGE_SIGNATURES_DEFAULT_HEIGHT;
				break;
			case 'questionslist-macro':
				height = QUESTIONS_LIST_DEFAULT_HEIGHT;
				break;
			case 'jiraroadmap':
				height = JIRA_ROADMAP_DEFAULT_HEIGHT;
				break;
			case 'lucidchart':
				height = LUCID_CHART_DEFAULT_HEIGHT;
				break;
			default:
				height = undefined;
				break;
		}
	}

	return {
		viewportHeight: height,
	};
};
